import type { ChannelMacros, TrackMeters, TrackState } from '@melodinai/types';

export interface MixSuggestion {
  id: string;
  text: string;
  action?: 'addPunch' | 'addWeight' | 'addAir' | 'addWidth' | 'fixMud' | 'improveClarity';
}

function nameOf(t: TrackState): string {
  return (t.name || '').toLowerCase();
}

export function suggestForTrack(
  track: TrackState | null | undefined,
  meters?: TrackMeters,
  allTracks?: TrackState[],
): MixSuggestion[] {
  if (!track || track.type === 'master' || track.type === 'return') return [];
  const macros: ChannelMacros = track.macros || { punch: 0, weight: 0, air: 0, width: 0.5 };
  const n = nameOf(track);
  const out: MixSuggestion[] = [];

  if ((n.includes('kick') || n.includes('bd')) && macros.weight < 0.35) {
    out.push({ id: 'kick-weight', text: 'Kick lacks low-end weight', action: 'addWeight' });
  }
  if ((n.includes('kick') || n.includes('bd')) && macros.punch < 0.3) {
    out.push({ id: 'kick-punch', text: 'Kick could use more punch', action: 'addPunch' });
  }
  if ((n.includes('bass') || n.includes('sub')) && macros.punch < 0.25) {
    out.push({ id: 'bass-ctrl', text: 'Bass needs tighter control', action: 'addPunch' });
  }
  if ((n.includes('vocal') || n.includes('vox')) && macros.air < 0.3) {
    out.push({ id: 'vox-air', text: 'Vocals could use more air', action: 'addAir' });
  }
  if ((n.includes('lead') || n.includes('synth')) && macros.width < 0.45) {
    out.push({ id: 'lead-width', text: 'Lead synth could be wider', action: 'addWidth' });
  }
  if ((n.includes('pad') || n.includes('atmos')) && macros.width < 0.55) {
    out.push({ id: 'pad-width', text: 'Pad wants more stereo width', action: 'addWidth' });
  }

  const peak = meters ? Math.max(meters.peakL, meters.peakR) : 0;
  if (peak > 0.92) {
    out.push({ id: 'hot', text: 'Channel is hot — reduce volume or add control', action: 'addPunch' });
  }

  if (allTracks) {
    const hasVocal = allTracks.some((t) => /vocal|vox/.test(nameOf(t)));
    const hasLead = /lead|synth|pluck/.test(n);
    if (hasVocal && hasLead && macros.air > 0.4) {
      out.push({ id: 'mask', text: 'Lead synth may be masking vocals', action: 'fixMud' });
    }
    const hasKick = allTracks.some((t) => /kick|bd/.test(nameOf(t)));
    if (hasKick && /bass|sub/.test(n) && macros.weight > 0.55) {
      out.push({ id: 'kb', text: 'Bass conflicts with kick', action: 'fixMud' });
    }
  }

  if (out.length === 0) {
    out.push({ id: 'clarity', text: 'Try Improve Clarity for more presence', action: 'improveClarity' });
  }

  return out.slice(0, 4);
}
